import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
} from '@mui/material';
import { 
  PersonAdd as PersonAddIcon, 
  Delete as DeleteIcon,
  Visibility as ViewAsIcon,
} from '@mui/icons-material';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useAuth } from '../../contexts/AuthContext';
import { AccountManager, UserProfile } from '../../types/database';

interface AccountManagerWithDetails extends AccountManager {
  manager_profile?: UserProfile;
  managed_user_profile?: UserProfile;
}

export function AccountManagerAssignment() {
  const supabase = useSupabaseClient();
  const { user, setViewingAs } = useAuth();
  const [assignments, setAssignments] = useState<AccountManagerWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState<UserProfile | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [availableUsers, setAvailableUsers] = useState<UserProfile[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const loadAssignments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('account_managers')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Fetch profiles for managers and managed users
      const assignmentsWithDetails = await Promise.all(
        (data || []).map(async (assignment) => {
          const [managerProfile, managedUserProfile] = await Promise.all([
            supabase
              .from('user_profiles')
              .select('*')
              .eq('user_id', assignment.manager_user_id)
              .maybeSingle(),
            supabase
              .from('user_profiles')
              .select('*')
              .eq('user_id', assignment.member_user_id)
              .maybeSingle(),
          ]);

          return {
            ...assignment,
            manager_profile: managerProfile.data || undefined,
            managed_user_profile: managedUserProfile.data || undefined,
          } as AccountManagerWithDetails;
        })
      );

      setAssignments(assignmentsWithDetails);
    } catch (err) {
      console.error('Error loading assignments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  const loadAvailableUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const { data, error: fetchError } = await supabase
        .from('user_profiles')
        .select('*')
        .order('full_name', { ascending: true });

      if (fetchError) throw fetchError;
      setAvailableUsers(data || []);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, [supabase]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const handleAssign = useCallback(async () => {
    if (!selectedManager || !selectedUser) {
      setError('Please select both a manager and a user');
      return;
    }

    if (selectedManager.user_id === selectedUser.user_id) {
      setError('A user cannot be assigned to manage themselves');
      return;
    }

    try {
      setError(null);
      const { error: insertError } = await supabase
        .from('account_managers')
        .insert({
          manager_user_id: selectedManager.user_id,
          member_user_id: selectedUser.user_id,
          created_by: user?.id,
        });

      if (insertError) throw insertError;

      setSuccess('Account manager assigned successfully');
      setAssignDialogOpen(false);
      setSelectedManager(null);
      setSelectedUser(null);
      await loadAssignments();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error assigning manager:', err);
      setError(err instanceof Error ? err.message : 'Failed to assign manager');
      setTimeout(() => setError(null), 5000);
    }
  }, [selectedManager, selectedUser, user?.id, supabase, loadAssignments]);

  const handleUnassign = useCallback(
    async (assignmentId: string) => {
      try {
        setError(null);
        const { error: deleteError } = await supabase
          .from('account_managers')
          .delete()
          .eq('id', assignmentId);

        if (deleteError) throw deleteError;

        setSuccess('Account manager unassigned successfully');
        await loadAssignments();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        console.error('Error unassigning manager:', err);
        setError(err instanceof Error ? err.message : 'Failed to unassign manager');
        setTimeout(() => setError(null), 5000);
      }
    },
    [supabase, loadAssignments]
  );

  const handleViewAs = useCallback(
    (managedUserId: string) => {
      setViewingAs(managedUserId);
      setSuccess('Now viewing as user account');
      setTimeout(() => setSuccess(null), 3000);
    },
    [setViewingAs]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Account Manager Assignments</Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => {
            loadAvailableUsers();
            setAssignDialogOpen(true);
          }}
        >
          Assign Manager
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Assign managers to oversee user accounts. Managers can view and manage the user's data.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Manager
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  User Account
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Assigned
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No account manager assignments yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              assignments.map((assignment) => (
                <TableRow key={assignment.id} hover>
                  <TableCell>
                    {assignment.manager_profile?.full_name || assignment.manager_profile?.signup_email || 'Unknown'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {assignment.managed_user_profile?.full_name || assignment.managed_user_profile?.signup_email || 'Unknown'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(assignment.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleViewAs(assignment.member_user_id)}
                      title="View as this user"
                      color="primary"
                    >
                      <ViewAsIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleUnassign(assignment.id)}
                      title="Unassign"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Account Manager</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <Autocomplete
              options={availableUsers.filter((u) => u.user_type === 'manager')}
              loading={loadingUsers}
              getOptionLabel={(option) =>
                `${option.full_name || 'No name'} (${option.signup_email || 'No email'})`
              }
              value={selectedManager}
              onChange={(_, newValue) => setSelectedManager(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Manager" placeholder="Select a manager" />
              )}
            />
            <Autocomplete
              options={availableUsers}
              loading={loadingUsers}
              getOptionLabel={(option) =>
                `${option.full_name || 'No name'} (${option.signup_email || 'No email'})`
              }
              value={selectedUser}
              onChange={(_, newValue) => setSelectedUser(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="User Account" placeholder="Select a user account" />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAssign} variant="contained" disabled={!selectedManager || !selectedUser}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

